import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Image from 'components/shared/image';
import Icon from 'components/shared/icon';
import SiteLink from 'components/shared/sitelink';
import { useAddItemsToCart, useUI } from 'hooks';
import useProductAddToCartTracking from 'hooks/tracking/useProductAddToCartTracking';
import { useGlobalModules } from 'contexts';
import { addLocaleToHref } from 'lib/utils/helpers';
import { formatPriceByStoreLocale, getCurrencyCode } from 'lib/shopify';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import { useRouter } from 'next/router';
import { buildUpsellTrackingPayload } from './buildUpsellTrackingPayload';
import { getSliderEdges } from './getSliderEdges';
import styles from './upsellSlider.module.scss';

// Cart-drawer upsell slider (#742 Phase 3). Custom drawer design (Cedric,
// 2026-06-17): header = title left + round prev/next arrows right; one product
// per slide on a #CFCFCF strip — image left, title / size / price stacked right,
// green (#008800) "+" add bottom-right. Presentational; the recommendation
// source lives in useCartUpsell, so this never changes when the source does.
const UpsellSlider = ({ items, locale }) => {
  const globalModules = useGlobalModules();
  const { translations, settings } = globalModules || {};
  const buttonColor = settings?.upsell?.buttonColor;
  const { closeCartPopup } = useUI();
  const addItems = useAddItemsToCart();
  const router = useRouter();
  const scrollerRef = useRef(null);
  const [adding, setAdding] = useState(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: false });

  const updateEdges = () => {
    const el = scrollerRef.current;
    if (el) setEdges(getSliderEdges(el));
  };

  useEffect(() => {
    updateEdges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const title =
    translations?.cart?.upsellTitle || translations?.product?.relatedProductsDefaultTitle || '';

  if (!items?.length) return null;

  const scrollByOne = direction => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  const handleAdd = async (variantId, item, index) => {
    if (!variantId || adding) return;
    setAdding(variantId);
    try {
      await addItems([{ variantId, quantity: 1 }]);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductAddToCartTracking({
        product: buildUpsellTrackingPayload(item, index, getCurrencyCode(locale)),
      });
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className={styles.upsell}>
      <div className={styles.head}>
        <p className={styles.title}>{title}</p>
        {items.length > 1 && (
          <div className={styles.arrows}>
            <button
              type="button"
              className={cn(styles.arrow, { [styles.disabled]: edges.atStart })}
              onClick={() => scrollByOne(-1)}
              aria-label={translations?.accessibility?.previous || 'Previous'}
            >
              <Icon type="chevron" className={styles.iconPrev} />
            </button>
            <button
              type="button"
              className={cn(styles.arrow, { [styles.disabled]: edges.atEnd })}
              onClick={() => scrollByOne(1)}
              aria-label={translations?.accessibility?.next || 'Next'}
            >
              <Icon type="chevron" className={styles.iconNext} />
            </button>
          </div>
        )}
      </div>

      <div className={styles.scroller} ref={scrollerRef} onScroll={updateEdges}>
        {items.map((item, index) => {
          const rawVariantId = item?.shopifyData?.variants?.[0]?.id;
          const variantId = (() => {
            if (!rawVariantId) return null;
            return String(rawVariantId).includes('gid://')
              ? rawVariantId
              : getShopifyGid('ProductVariant', rawVariantId);
          })();
          const price =
            item?.cardShopifyData?.productPrice ?? item?.shopifyData?.variants?.[0]?.price;
          const compareAt = item?.cardShopifyData?.productCompareAtPrice;
          const href = item?.slug ? addLocaleToHref(`p/${item.slug}`, locale, router) : null;

          return (
            <div className={styles.slide} key={item.slug || rawVariantId}>
              <SiteLink
                locale={locale}
                href={href || '#'}
                onClick={closeCartPopup}
                className={styles.image}
              >
                <Image src={item.image} layout="fill" sizes="25vw" />
              </SiteLink>

              <div className={styles.product}>
                <div className={styles.info}>
                  <div className={styles.heading}>
                    <SiteLink
                      locale={locale}
                      href={href || '#'}
                      onClick={closeCartPopup}
                      className={styles.name}
                    >
                      {item.title}
                    </SiteLink>
                    {item?.size ? <span className={styles.size}>{item.size}</span> : null}
                  </div>
                  <span className={styles.price}>
                    {compareAt ? (
                      <s className={styles.compareAt}>
                        {formatPriceByStoreLocale({ locale, price: compareAt })}
                      </s>
                    ) : null}
                    {price != null ? formatPriceByStoreLocale({ locale, price }) : null}
                  </span>
                </div>

                <button
                  type="button"
                  className={styles.addBtn}
                  style={buttonColor ? { backgroundColor: buttonColor } : undefined}
                  onClick={() => handleAdd(variantId, item, index)}
                  disabled={!variantId || adding === variantId}
                  aria-label={translations?.button?.addToCart || 'Add to cart'}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpsellSlider;
